import { Check, PenLine, Plus } from "lucide-react";
import { useState } from "react";
import { Belief } from "@/components/Belief";
import { ApiError, type BeliefAssertion, type InsightCategory, type SubjectBelief, type SubjectSection } from "@/lib/api";
import { today as localToday } from "@/lib/format";
import { INSIGHT_CATEGORIES } from "@/lib/insightCategory";

/**
 * UI-20 — the owner's hand on a page's beliefs, shared by every page that has beliefs: the
 * World view subject page, a person's dossier, an entity's detail and an initiative's dossier.
 *
 * UI-19 built this on the initiative dossier; M17.2 moved it up a level rather than copying it,
 * so "add a belief" means one thing everywhere. What it adds over UI-19 is what the owner now
 * says about a belief: the **section** it is filed under (INS-1's closed list — a belief the
 * owner types is a decision or a deadline as often as a fact) and **when it started holding**,
 * because "Renée has been my lead since 2025" is a belief with a window like any other.
 *
 * A belief is never edited in place. **Correct** is the owner's `supersede` of it — machine- or
 * owner-sourced alike — which closes its window and points it at the new statement, so the old
 * one stays readable under Superseded with a link forward.
 */

type Submit = (input: BeliefAssertion) => Promise<void>;

/** Every current belief of a page under its heading, each correctable, and the add form. */
export function BeliefsEditor({
  sections,
  slug,
  onAssert,
  title = "Beliefs",
  hint = "What is held about this — from sources, or typed in by you.",
  empty = "Nothing recorded yet. Add the first one below, or let it fill in as sources mention it.",
}: {
  sections: SubjectSection[];
  slug: string;
  onAssert: Submit;
  title?: string;
  hint?: string;
  empty?: string;
}) {
  const current = sections.reduce((total, section) => total + section.beliefs.length, 0);

  return (
    <section className="flex flex-col gap-[var(--space-4)]">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h2 className="font-semibold text-[var(--text-primary)]">{title}</h2>
        <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">{hint}</span>
      </div>

      {current === 0 && <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">{empty}</p>}

      {sections.map((section) => (
        <div key={section.anchor} className="flex flex-col gap-[var(--space-3)]">
          <h3 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-secondary)]">{section.heading}</h3>
          {section.beliefs.map((belief) => (
            <CorrectableBelief key={belief.id} belief={belief} slug={slug} onAssert={onAssert} />
          ))}
        </div>
      ))}

      <AddBelief onAssert={onAssert} />
    </section>
  );
}

/** One belief with a quiet "Correct" affordance while it is current. */
export function CorrectableBelief({
  belief,
  slug,
  onAssert,
}: {
  belief: SubjectBelief;
  slug: string;
  onAssert: Submit;
}) {
  const [correcting, setCorrecting] = useState(false);

  if (correcting) {
    return (
      <BeliefForm
        initialStatement={belief.statement}
        initialCategory={belief.category}
        statementLabel="Corrected statement"
        submitLabel="Correct"
        note="Closes this belief and opens the corrected statement in its place. The old one stays under Superseded."
        onCancel={() => setCorrecting(false)}
        onSubmit={async (input) => {
          await onAssert({ ...input, supersedesBeliefId: belief.id });
          setCorrecting(false);
        }}
      />
    );
  }

  return (
    <div className="group relative">
      <Belief belief={belief} slug={slug} />
      {!belief.validTo && (
        <button
          type="button"
          onClick={() => setCorrecting(true)}
          aria-label={`Correct “${belief.statement}”`}
          className="absolute right-0 top-0 inline-flex items-center gap-1 rounded-full border border-transparent px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--text-muted)] opacity-0 transition hover:border-[var(--border-subtle)] hover:text-[var(--vl-bridge-soft)] focus-visible:opacity-100 group-hover:opacity-100"
        >
          <PenLine size={11} strokeWidth={2} />
          Correct
        </button>
      )}
    </div>
  );
}

/** The dashed "Add a belief" pill, opening into the form. */
export function AddBelief({ onAssert, label = "Add a belief" }: { onAssert: Submit; label?: string }) {
  const [adding, setAdding] = useState(false);

  if (!adding) {
    return (
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="inline-flex w-fit items-center gap-1.5 rounded-full border border-dashed border-[var(--border-default)] px-3 py-1.5 text-[length:var(--text-sm)] text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--vl-bridge-soft)]"
      >
        <Plus size={14} strokeWidth={2} />
        {label}
      </button>
    );
  }

  return (
    <BeliefForm
      initialStatement=""
      initialCategory="fact"
      statementLabel="New belief"
      submitLabel="Add"
      placeholder="A decision, a deadline, a fact — whatever is true, in one sentence."
      onCancel={() => setAdding(false)}
      onSubmit={async (input) => {
        await onAssert(input);
        setAdding(false);
      }}
    />
  );
}

function BeliefForm({
  initialStatement,
  initialCategory,
  statementLabel,
  submitLabel,
  placeholder,
  note,
  onCancel,
  onSubmit,
}: {
  initialStatement: string;
  initialCategory: InsightCategory;
  statementLabel: string;
  submitLabel: string;
  placeholder?: string;
  note?: string;
  onCancel: () => void;
  onSubmit: Submit;
}) {
  const [statement, setStatement] = useState(initialStatement);
  const [category, setCategory] = useState<InsightCategory>(initialCategory);
  const [validFrom, setValidFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // The owner's own calendar day, not UTC's: after midnight in Amsterdam UTC is still on yesterday.
  const today = localToday();

  async function submit() {
    const trimmed = statement.trim();
    if (trimmed.length === 0 || busy) return;

    setBusy(true);
    setProblem(null);
    try {
      // Today is "from now": a bare date is midnight, which the Server may read as still to come.
      await onSubmit({ statement: trimmed, category, ...(validFrom && validFrom !== today ? { validFrom } : {}) });
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2.5 rounded-[var(--radius-md)] border border-dashed border-[var(--border-default)] bg-[var(--surface-glass)] p-[var(--space-3)]">
      <textarea
        autoFocus
        rows={2}
        value={statement}
        onChange={(event) => setStatement(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") onCancel();
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) void submit();
        }}
        placeholder={placeholder}
        aria-label={statementLabel}
        className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-3 py-2 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
      />

      <div className="flex flex-wrap items-center gap-x-[var(--space-4)] gap-y-2 text-[length:var(--text-xs)] text-[var(--text-muted)]">
        <label className="inline-flex items-center gap-1.5">
          Filed under
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value as InsightCategory)}
            aria-label="Category"
            className="h-7 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-2 text-[length:var(--text-xs)] text-[var(--text-primary)]"
          >
            {INSIGHT_CATEGORIES.map((meta) => (
              <option key={meta.category} value={meta.category}>
                {meta.label}
              </option>
            ))}
          </select>
        </label>

        <label className="inline-flex items-center gap-1.5">
          Holding since
          <input
            type="date"
            value={validFrom}
            max={today}
            onChange={(event) => setValidFrom(event.target.value)}
            aria-label="Valid from"
            className="h-7 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-2 text-[length:var(--text-xs)] text-[var(--text-primary)] [color-scheme:inherit]"
          />
          {!validFrom && <span>(today)</span>}
        </label>
      </div>

      {problem && (
        <span role="alert" className="text-[length:var(--text-2xs)] text-[var(--warning)]">
          {problem}
        </span>
      )}

      <div className="flex items-center gap-2">
        {note && <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">{note}</span>}
        <span className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-8 rounded-[var(--radius-md)] px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || statement.trim().length === 0}
            onClick={() => void submit()}
            className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] disabled:opacity-50"
          >
            <Check size={14} strokeWidth={2.5} />
            {submitLabel}
          </button>
        </span>
      </div>
    </div>
  );
}
